<script setup lang="ts">
import { storedChapterName } from "../../lib/chapterNumbering";
import { useChapterHeadings } from "../../composables/useChapterHeadings";
import {
  mapLocationChipAsEntity,
  useSceneMetadata,
} from "../../composables/useSceneMetadata";
import { trimmedMaybe } from "../../lib/trimmedString";
import {
  andThenMaybe,
  atIndexMaybe,
  nothing,
  just,
  toDomAttr,
  toMaybe,
  isJust,
  isNothing,
  type Maybe,
} from "../../lib/maybe";
import { computed, nextTick, useTemplateRef } from "vue";
import { useEditorSessionStore } from "../../stores/useEditorSessionStore";
import { useProjectWorkspaceStore } from "../../stores/useProjectWorkspaceStore";
import { useOutlineEntitiesStore } from "../../stores/useOutlineEntitiesStore";
import { useSceneOpeningsStore } from "../../stores/useSceneOpeningsStore";
import {
  sceneCustomName,
  sceneLabelText,
  unknownOpening,
} from "../../lib/sceneLabel";
import {
  END_OF_MANUSCRIPT_DROP_TARGET,
  useStoryboardDragStore,
  type BoardDragSource,
} from "../../stores/useStoryboardDragStore";
import {
  ensureElementInScroller,
  ensureSelectionInScroller,
} from "../../lib/domScroll";
import { chapterMapPreviews } from "../../lib/storyboardMapLocations";
import { requestMapLandmarkFocus } from "../../lib/mapLandmarkFocus";
import { useStoryboardMapLocations } from "../../composables/useStoryboardMapLocations";
import {
  actFolderForChapter,
  actPrimaryPath,
  chapterPrimaryPath,
  scenePath,
  type ZwAct,
  type ZwChapter,
} from "../../lib/zwConfig";
import StoryboardCreate from "./StoryboardCreate.vue";
import StoryboardName from "./StoryboardName.vue";
import { IconGripVertical, IconTimeline } from "../ribbonIcons";
import StoryboardCard, {
  type StoryboardEntityChip,
} from "./StoryboardCard.vue";

import TimelineFilters from "./TimelineFilters.vue";
import { filterTimelineManuscript } from "../../lib/timelineFilters";
import { useTea } from "../../lib/tea/useTea";
import { initialModel, update } from "./StoryboardBoard.update";
const { chapterLabel, actLabel, canRenameAct, actRenamePrefix } =
  useChapterHeadings();
const { model, dispatch } = useTea(initialModel, update);
const filtering = computed(() => model.value.filters.entries.length > 0);

const session = useEditorSessionStore();
const workspace = useProjectWorkspaceStore();
const sceneOpenings = useSceneOpeningsStore();
const outlineEntities = useOutlineEntitiesStore();
const { index: mapLocationIndex, ready: mapsReady } =
  useStoryboardMapLocations();

const timelineReady = computed(() => mapsReady.value && sceneOpenings.ready);

const chapters = computed<ZwChapter[]>(() => session.chapters);

/** The board renders the manuscript as a run of sections: an act (labeled
 * header plus its chapters) or a single ungrouped chapter. Flattening to
 * sections keeps the chapter/scene rendering below identical for both cases. */
type BoardSection =
  | { kind: "act"; act: ZwAct; chapters: ZwChapter[] }
  | { kind: "chapter"; chapter: ZwChapter };

const sections = computed<BoardSection[]>(() =>
  visibleManuscript.value.flatMap((item): BoardSection[] => {
    if (item.type === "act") {
      return [{ kind: "act", act: item, chapters: item.chapters }];
    }
    if (item.type === "map") return [];
    return [{ kind: "chapter", chapter: item }];
  }),
);

const showUngroupedChapterCreate = computed(() => {
  const last = atIndexMaybe(session.manuscript, session.manuscript.length - 1);
  return isNothing(last) || last.value.type !== "act";
});

// Acts collapse from the same `.zwlocal` list the outline writes, so folding
// an act in one view folds it in the other.
function isActCollapsed(act: ZwAct): boolean {
  return (
    filtering.value === false &&
    session.collapsedActs.includes(actPrimaryPath(act))
  );
}

function toggleActCollapse(act: ZwAct): void {
  void workspace.onToggleActCollapsed(actPrimaryPath(act));
}

function actTitle(act: ZwAct): string {
  return actLabel(act.folder);
}

function actDragSource(act: ZwAct): BoardDragSource {
  return {
    key: `act:${act.folder}`,
    kind: "act",
    folder: just(act.folder),
    actFolder: nothing,
    sceneFile: nothing,
    name: nothing,
    entryFile: nothing,
    momentId: nothing,
  };
}

/** Render order: an act header followed by its chapters (unless folded), or a
 * bare ungrouped chapter. Flat rather than nested so a chapter's markup is
 * written once, grouped or not. */
type BoardRow =
  | { kind: "act"; act: ZwAct; chapterCount: number }
  | { kind: "act-end"; act: ZwAct; chapterCount: number }
  | { kind: "ungrouped-end"; before: string }
  | { kind: "chapter"; chapter: ZwChapter; actFolder: Maybe<string> };

const boardRows = computed<BoardRow[]>(() => {
  const rows: BoardRow[] = [];
  for (const section of sections.value) {
    if (section.kind === "chapter") {
      rows.push({
        kind: "chapter",
        chapter: section.chapter,
        actFolder: nothing,
      });
      continue;
    }
    const previous = atIndexMaybe(rows, rows.length - 1);
    if (isJust(previous) && previous.value.kind === "chapter") {
      rows.push({ kind: "ungrouped-end", before: section.act.folder });
    }
    rows.push({
      kind: "act",
      act: section.act,
      chapterCount: section.chapters.length,
    });
    if (isActCollapsed(section.act)) continue;
    for (const chapter of section.chapters) {
      rows.push({
        kind: "chapter",
        chapter,
        actFolder: just(section.act.folder),
      });
    }
    rows.push({
      kind: "act-end",
      act: section.act,
      chapterCount: section.chapters.length,
    });
  }
  return rows;
});

// --- Collapse a multi-scene chapter's band down to just its header ---------
// Backed by `session.collapsedChapters` (browser localStorage via
// `localPrefs`, identified by `chapterPrimaryPath`). Same live ref
// `OutlineTree.vue`'s chapter-collapse chevron reads/writes, so toggling
// here updates the sidebar tree immediately.
function isChapterCollapsed(chapter: ZwChapter): boolean {
  return (
    filtering.value === false &&
    session.collapsedChapters.includes(chapterPrimaryPath(chapter))
  );
}

function toggleChapterCollapse(chapter: ZwChapter): void {
  const key = chapterPrimaryPath(chapter);
  const current = session.collapsedChapters;
  const next = current.includes(key)
    ? current.filter((k) => k !== key)
    : [...current, key];
  void workspace.onSetCollapsedChapters(next);
}

function chapterTitle(chapter: ZwChapter): string {
  return chapterLabel(chapter.folder);
}

const mapOrder = computed(() =>
  session.manuscript.flatMap((item) =>
    item.type === "map" ? [item.mapPath.replace(/^Database\//, "")] : [],
  ),
);

const mapPreviews = computed(() =>
  chapterMapPreviews(
    visibleManuscript.value.flatMap((item) =>
      item.type === "act"
        ? item.chapters
        : item.type === "chapter"
          ? [item]
          : [],
    ),
    mapLocationIndex.value,
    mapOrder.value,
  ),
);

const metadata = useSceneMetadata();
function entityChipsFor(sceneFilePath: string): StoryboardEntityChip[] {
  return [
    ...(mapPreviews.value.get(sceneFilePath) ?? []).map(
      mapLocationChipAsEntity,
    ),
    ...metadata.chipsFor(sceneFilePath).filter((chip) => chip.kind !== "map"),
  ];
}

const sceneEntries = computed(
  () =>
    new Map(
      chapters.value.flatMap((chapter) =>
        chapter.scenes.map((scene) => {
          const path = scenePath(chapter.folder, scene.file);
          return [
            path,
            metadata
              .chipsFor(path)
              .flatMap((chip) =>
                isJust(chip.entryFile) ? [chip.entryFile.value] : [],
              ),
          ] as const;
        }),
      ),
    ),
);
const visibleManuscript = computed(() =>
  filterTimelineManuscript(
    session.manuscript,
    sceneEntries.value,
    model.value.filters,
  ),
);
const visibleChapters = computed(() =>
  visibleManuscript.value.flatMap((item) =>
    item.type === "act" ? item.chapters : item.type === "chapter" ? [item] : [],
  ),
);
const visibleActCount = computed(
  () => visibleManuscript.value.filter((item) => item.type === "act").length,
);
const matchingScenes = computed(() =>
  visibleManuscript.value.reduce(
    (count, item) =>
      count +
      (item.type === "chapter"
        ? item.scenes.length
        : item.type === "act"
          ? item.chapters.reduce((n, chapter) => n + chapter.scenes.length, 0)
          : 0),
    0,
  ),
);

function onCardPointerDown(source: BoardDragSource, event: PointerEvent): void {
  if (filtering.value) return;
  drag.onCardPointerDown(source, event);
}

function onChipClick(chip: StoryboardEntityChip): void {
  if (chip.kind === "map") {
    const mapFile = andThenMaybe(chip.mapFile, (value) => trimmedMaybe(value));
    const landmarkId = andThenMaybe(chip.landmarkId, (value) =>
      trimmedMaybe(value),
    );
    if (isNothing(mapFile) || isNothing(landmarkId)) return;
    requestMapLandmarkFocus(mapFile.value, landmarkId.value);
    void workspace.onSelectProjectDatabase(mapFile.value);
    if (session.mobileShell) {
      session.contextPanelOpen = false;
    }
    return;
  }
  if (
    chip.kind !== "arc" &&
    chip.kind !== "character" &&
    chip.kind !== "entity"
  )
    return;
  const entryFile = andThenMaybe(chip.entryFile, (value) =>
    trimmedMaybe(value),
  );
  if (isNothing(entryFile)) return;
  void workspace.onSelectProjectDatabase(entryFile.value);
}

function onArcChipPointerDown(
  chip: StoryboardEntityChip,
  event: PointerEvent,
): void {
  if (chip.kind !== "arc") return;
  const entryFile = andThenMaybe(chip.entryFile, (value) =>
    trimmedMaybe(value),
  );
  const momentId = andThenMaybe(chip.momentId, (value) => trimmedMaybe(value));
  if (isNothing(entryFile) || isNothing(momentId)) return;
  drag.onCardPointerDown(
    {
      key: `arc-moment:${entryFile.value}:${momentId.value}`,
      kind: "arc-moment",
      folder: nothing,
      actFolder: nothing,
      sceneFile: nothing,
      name: nothing,
      entryFile,
      momentId,
    },
    event,
  );
}

async function openScene(path: string): Promise<void> {
  await workspace.onSelectProjectFile(path);
}

/** Announced by the board's `aria-live` region after every keyboard-driven
 * move — drag has visual feedback for sighted users, keyboard reorder has
 * none unless something narrates the result. */

function announce(text: string): void {
  void dispatch({ kind: "Announce", text });
}

type Direction = "left" | "right" | "up" | "down";

/** Alt+Up/Down follows the timeline within a chapter; Alt+Left/Right
 * moves to the end of the previous/next chapter. */
function onSceneKeyboardMove(
  chapter: ZwChapter,
  sceneFile: string,
  direction: Direction,
): void {
  if (filtering.value) return;
  const sceneIndex = chapter.scenes.findIndex((s) => s.file === sceneFile);
  if (sceneIndex < 0) return;
  const sceneM = atIndexMaybe(chapter.scenes, sceneIndex);
  if (isNothing(sceneM)) return;
  const scene = sceneM.value;
  const label = sceneLabelText(
    sceneOpenings.labelFor(scene, scenePath(chapter.folder, scene.file)),
  );

  if (direction === "up" || direction === "down") {
    const targetIndex = direction === "up" ? sceneIndex - 1 : sceneIndex + 1;
    if (targetIndex < 0 || targetIndex >= chapter.scenes.length) return;
    const targetM = atIndexMaybe(chapter.scenes, targetIndex);
    if (isNothing(targetM)) return;
    void workspace.onReorderProjectScenes(
      { folder: chapter.folder, sceneFile },
      { folder: chapter.folder, sceneFile: just(targetM.value.file) },
    );
    announce(
      `${label} moved to position ${targetIndex + 1} of ${chapterTitle(chapter)}.`,
    );
    return;
  }

  const chapterIndex = chapters.value.findIndex(
    (c) => c.folder === chapter.folder,
  );
  if (chapterIndex < 0) return;
  const targetChapterIndex =
    direction === "left" ? chapterIndex - 1 : chapterIndex + 1;
  const targetChapterM = atIndexMaybe(chapters.value, targetChapterIndex);
  if (isNothing(targetChapterM)) return;
  const targetChapter = targetChapterM.value;
  void workspace.onReorderProjectScenes(
    { folder: chapter.folder, sceneFile },
    { folder: targetChapter.folder, sceneFile: nothing },
  );
  announce(`${label} moved to the end of ${chapterTitle(targetChapter)}.`);
}

/** Alt+Up/Down on a chapter card (flat or band header) — swap with the
 * previous/next chapter in reading order, the same slot commit a chapter drag
 * would make. Moving past an act boundary moves the chapter into (or out of)
 * that act, which is what "the next slot" means once acts exist. */
function onChapterKeyboardMove(chapter: ZwChapter, direction: Direction): void {
  if (filtering.value || direction === "left" || direction === "right") return;
  const order = chapters.value;
  const chapterIndex = order.findIndex((c) => c.folder === chapter.folder);
  if (chapterIndex < 0) return;
  const targetIndex = direction === "up" ? chapterIndex - 1 : chapterIndex + 1;
  const targetM = atIndexMaybe(order, targetIndex);
  if (isNothing(targetM)) return;
  const target = targetM.value;
  const targetAct = actFolderForChapter(session.manuscript, target.folder);

  // Exact slot of the neighbor — same commit a drag onto that chapter uses.
  // (Previously "down" aimed past the neighbor to compensate for insert-before
  // semantics; exact-slot makes both directions just target the neighbor.)
  void workspace.onMoveChapterToSlot(chapter.folder, {
    actFolder: targetAct,
    beforeFolder: just(target.folder),
  });
  announce(
    `${chapterTitle(chapter)} moved to position ${targetIndex + 1} of ${order.length}.`,
  );
}

/** Alt+Up/Down on an act header: swap with the previous/next act or ungrouped
 * chapter, carrying its own chapters along. */
function onActKeyboardMove(act: ZwAct, direction: Direction): void {
  if (filtering.value || direction === "left" || direction === "right") return;
  const index = sections.value.findIndex(
    (s) => s.kind === "act" && s.act.folder === act.folder,
  );
  if (index < 0) return;
  const targetIndex = direction === "up" ? index - 1 : index + 1;
  const targetM = atIndexMaybe(sections.value, targetIndex);
  if (isNothing(targetM)) return;
  const target = targetM.value;
  // Down means past the neighbor, so aim at whatever follows it (nothing = end).
  const beforeSectionM =
    direction === "up"
      ? targetM
      : atIndexMaybe(sections.value, targetIndex + 1);
  let before: Maybe<string> = nothing;
  if (isJust(beforeSectionM)) {
    const beforeSection = beforeSectionM.value;
    if (beforeSection.kind === "act") before = just(beforeSection.act.folder);
    else before = just(beforeSection.chapter.folder);
  }

  void workspace.onMoveAct(act.folder, before);
  const targetLabel =
    target.kind === "act" ? actTitle(target.act) : chapterTitle(target.chapter);
  announce(
    `${actTitle(act)} moved ${direction === "up" ? "before" : "after"} ${targetLabel}.`,
  );
}

function chapterDragSource(
  chapter: ZwChapter,
  actFolder: Maybe<string>,
): BoardDragSource {
  return {
    key: `chapter:${chapter.folder}`,
    kind: "chapter",
    folder: just(chapter.folder),
    actFolder,
    sceneFile: nothing,
    name: nothing,
    entryFile: nothing,
    momentId: nothing,
  };
}

function sceneDragSource(
  chapter: ZwChapter,
  sceneFile: string,
  actFolder: Maybe<string>,
): BoardDragSource {
  return {
    key: `scene:${chapter.folder}:${sceneFile}`,
    kind: "scene",
    folder: just(chapter.folder),
    actFolder,
    sceneFile: just(sceneFile),
    name: nothing,
    entryFile: nothing,
    momentId: nothing,
  };
}

function tailKeyFor(chapter: ZwChapter): string {
  return `tail:${chapter.folder}`;
}

const drag = useStoryboardDragStore();

function isDragOverKey(key: string): boolean {
  return isJust(drag.dragOver) && drag.dragOver.value === key;
}

function isDragFromKey(key: string): boolean {
  return isJust(drag.dragFrom) && drag.dragFrom.value.key === key;
}

function isDraggingKind(...kinds: BoardDragSource["kind"][]): boolean {
  return isJust(drag.dragKind) && kinds.includes(drag.dragKind.value);
}

const draggedChapterSectionEnd = computed(() => {
  let found = false;
  for (const row of boardRows.value) {
    if (
      row.kind === "chapter" &&
      isDragFromKey(`chapter:${row.chapter.folder}`)
    ) {
      found = true;
    }
    if (found && row.kind === "ungrouped-end")
      return `ungrouped-end:${row.before}`;
    if (found && row.kind === "act-end") return `act-end:${row.act.folder}`;
  }
  return END_OF_MANUSCRIPT_DROP_TARGET;
});

function showChapterBoundary(key: string): boolean {
  return isDraggingKind("chapter") && draggedChapterSectionEnd.value !== key;
}

// --- Create a new chapter or act — lightweight inline flows (no template
// picker). The outline `+` dropdown covers the richer create menu. ---------

const boardEl = useTemplateRef<HTMLElement>("board");

/** After layout settles (new card mounted, synopsis autosize), pin content. */
async function afterLayout(run: () => void): Promise<void> {
  await nextTick();
  requestAnimationFrame(run);
}

function scrollSelectionIntoBoard(): void {
  const host = toMaybe(boardEl.value);
  if (isNothing(host)) return;
  ensureSelectionInScroller(host.value);
}

function scrollElementIntoBoard(el: Maybe<Element>): void {
  const host = toMaybe(boardEl.value);
  if (isNothing(host) || isNothing(el)) return;
  ensureElementInScroller(host.value, el.value);
}

async function addSceneToChapter(folder: string): Promise<void> {
  await workspace.onNewProjectScene(folder);
  await afterLayout(() => {
    const host = toMaybe(boardEl.value);
    if (isNothing(host)) return;
    const sceneCards = host.value.querySelectorAll(
      `[data-board-kind="scene"][data-board-folder="${CSS.escape(folder)}"]`,
    );
    const lastScene = toMaybe(sceneCards[sceneCards.length - 1]);
    if (isJust(lastScene)) {
      scrollElementIntoBoard(lastScene);
      return;
    }
    // Flat chapter just grew its first extra scene and became a band.
    scrollElementIntoBoard(
      toMaybe(
        host.value.querySelector(
          `[data-board-kind="chapter"][data-board-folder="${CSS.escape(folder)}"]`,
        ),
      ),
    );
  });
}

/** Band headers have no synopsis field, so nothing ever commits one. */
function ignoreChapterSynopsis(): void {}
</script>

<template>
  <div
    ref="board"
    class="zw-storyboard-board"
    :class="{
      'zw-storyboard-board-dragging': isJust(drag.dragFrom),
      'zw-storyboard-filtered': filtering,
      'zw-storyboard-loading': timelineReady === false,
    }"
  >
    <header class="zw-storyboard-heading">
      <h1>{{ session.projectName }}</h1>
      <span class="zw-storyboard-counts" aria-live="polite">
        {{ visibleActCount }} {{ visibleActCount === 1 ? "act" : "acts" }} ·
        {{ visibleChapters.length }}
        {{ visibleChapters.length === 1 ? "chapter" : "chapters" }} ·
        {{ matchingScenes }} {{ matchingScenes === 1 ? "scene" : "scenes" }}
      </span>
      <TimelineFilters
        :filters="model.filters"
        :entries="outlineEntities.databaseEntryFields"
        @change="dispatch({ kind: 'Filters', filters: $event })"
      />
    </header>
    <p
      v-if="timelineReady && filtering && matchingScenes === 0"
      class="zw-storyboard-empty"
    >
      No scenes match these filters. Remove a filter to show more scenes.
    </p>
    <div
      v-if="timelineReady === false"
      role="status"
      class="zw-timeline-loading"
    >
      <IconTimeline aria-hidden="true" />
      <span>Loading timeline…</span>
    </div>
    <div v-show="timelineReady" class="zw-storyboard-timeline">
      <p v-if="!filtering && sections.length === 0" class="zw-storyboard-empty">
        No chapters yet.
      </p>
      <template
        v-for="row in boardRows"
        :key="
          row.kind === 'ungrouped-end'
            ? `ungrouped-end:${row.before}`
            : row.kind === 'act' || row.kind === 'act-end'
              ? `${row.kind}:${row.act.folder}`
              : row.chapter.folder
        "
      >
        <!-- An act reads as a labeled section rule, not another card: it has a
           title and nothing else to show. -->
        <div
          v-if="row.kind === 'act'"
          class="zw-board-act-header"
          :class="{
            'zw-board-act-header-over': isDragOverKey(
              actDragSource(row.act).key,
            ),
            'zw-board-act-header-drag': isDragFromKey(
              actDragSource(row.act).key,
            ),
          }"
          :data-board-key="actDragSource(row.act).key"
          data-board-kind="act"
          :data-board-folder="row.act.folder"
          role="group"
          :aria-label="`Act: ${actTitle(row.act)}`"
          tabindex="0"
          :aria-keyshortcuts="
            toDomAttr(filtering ? nothing : just('Alt+ArrowUp Alt+ArrowDown'))
          "
          @keydown.alt.up.prevent="onActKeyboardMove(row.act, 'up')"
          @keydown.alt.down.prevent="onActKeyboardMove(row.act, 'down')"
        >
          <div class="zw-timeline-heading-group">
            <button
              type="button"
              class="zw-board-act-collapse"
              :aria-expanded="!isActCollapsed(row.act)"
              :aria-label="
                isActCollapsed(row.act) ? 'Expand act' : 'Collapse act'
              "
              @click.stop="toggleActCollapse(row.act)"
            >
              <span v-if="isActCollapsed(row.act)" class="zw-board-act-count">
                {{ row.chapterCount }} chapters
              </span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.75"
                class="size-3"
                :class="{ 'twist-open': !isActCollapsed(row.act) }"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="m9 6 6 6-6 6"
                />
              </svg>
            </button>
            <button
              type="button"
              class="zw-board-act-grip"
              aria-label="Drag act"
              @pointerdown.stop="
                onCardPointerDown(actDragSource(row.act), $event)
              "
            >
              <IconGripVertical class="size-4" />
            </button>
            <div class="zw-board-act-title">
              <StoryboardName
                v-if="canRenameAct(row.act.folder)"
                :prefix="actRenamePrefix(row.act.folder)"
                :name="storedChapterName(row.act)"
                label="Act name"
                :on-rename="
                  (name: string) =>
                    workspace.onRenameProjectAct(row.act.folder, name)
                "
                >{{ actTitle(row.act) }}</StoryboardName
              >
              <span v-else>{{ actTitle(row.act) }}</span>
            </div>
            <button
              type="button"
              class="zw-board-act-delete"
              aria-label="Delete act"
              @click.stop="workspace.onDeleteProjectAct(row.act.folder)"
            >
              Delete
            </button>
          </div>
        </div>

        <div v-else-if="row.kind === 'ungrouped-end'">
          <div
            class="zw-storyboard-end-drop"
            :class="{
              'zw-storyboard-end-drop-idle': !showChapterBoundary(
                `ungrouped-end:${row.before}`,
              ),
              'zw-storyboard-end-drop-over': isDragOverKey(
                `ungrouped-end:${row.before}`,
              ),
            }"
            :data-board-key="`ungrouped-end:${row.before}`"
            data-board-kind="chapter-boundary"
            :data-board-folder="row.before"
          />
        </div>
        <div v-else-if="row.kind === 'act-end'" class="zw-board-act-end">
          <div
            class="zw-storyboard-end-drop"
            :class="{
              'zw-storyboard-end-drop-idle':
                row.chapterCount === 0 ||
                !showChapterBoundary(`act-end:${row.act.folder}`),
              'zw-storyboard-end-drop-over': isDragOverKey(
                `act-end:${row.act.folder}`,
              ),
            }"
            :data-board-key="`act-end:${row.act.folder}`"
            data-board-kind="act"
            :data-board-folder="row.act.folder"
          />
          <StoryboardCreate
            v-if="!filtering"
            :data-act-create="row.act.folder"
            kind="Chapter"
            @create="
              workspace.onNewStoryboardChapter($event, just(row.act.folder))
            "
          />
          <div class="zw-board-act-end-label">
            <span>End of {{ actTitle(row.act) }}</span>
          </div>
        </div>
        <section
          v-else-if="row.kind === 'chapter'"
          class="zw-board-band"
          :class="{ 'zw-board-in-act': isJust(row.actFolder) }"
          role="group"
          :aria-label="`Chapter: ${chapterTitle(row.chapter)}`"
        >
          <div class="zw-board-band-header-wrap">
            <div class="zw-timeline-heading-group">
              <StoryboardCard
                :reorder-enabled="!filtering"
                class="zw-board-band-header"
                kind="chapter"
                :title="just(storedChapterName(row.chapter))"
                hide-synopsis
                synopsis=""
                :opening="unknownOpening"
                :synopsis-id="row.chapter.folder"
                :entity-chips="[]"
                :drag-source="chapterDragSource(row.chapter, row.actFolder)"
                :is-drag-over="
                  isDragOverKey(
                    chapterDragSource(row.chapter, row.actFolder).key,
                  )
                "
                :is-drag-source="
                  isDragFromKey(
                    chapterDragSource(row.chapter, row.actFolder).key,
                  )
                "
                :on-commit-synopsis="ignoreChapterSynopsis"
                :on-rename="
                  (to: string) =>
                    workspace.onRenameProjectFile(
                      chapterPrimaryPath(row.chapter),
                      to,
                    )
                "
                :on-card-pointer-down="onCardPointerDown"
                :on-keyboard-move="
                  (direction: Direction) =>
                    onChapterKeyboardMove(row.chapter, direction)
                "
                :on-park="() => workspace.onParkChapter(row.chapter.folder)"
                :on-delete="
                  () =>
                    workspace.onDeleteProjectFile(
                      chapterPrimaryPath(row.chapter),
                    )
                "
                :on-add-scene="nothing"
                :on-chip-click="onChipClick"
                :on-arc-chip-pointer-down="onArcChipPointerDown"
                @autosize="scrollSelectionIntoBoard"
              />
              <button
                type="button"
                class="zw-board-band-collapse"
                :aria-expanded="!isChapterCollapsed(row.chapter)"
                :aria-label="
                  isChapterCollapsed(row.chapter)
                    ? 'Expand scenes'
                    : 'Collapse scenes'
                "
                :title="
                  isChapterCollapsed(row.chapter)
                    ? 'Expand scenes'
                    : 'Collapse scenes'
                "
                @click.stop="toggleChapterCollapse(row.chapter)"
              >
                <span
                  v-if="isChapterCollapsed(row.chapter)"
                  class="zw-board-band-collapse-count"
                >
                  {{ row.chapter.scenes.length }} scenes
                </span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.75"
                  class="size-3"
                  :class="{ 'twist-open': !isChapterCollapsed(row.chapter) }"
                  aria-hidden="true"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="m9 6 6 6-6 6"
                  />
                </svg>
              </button>
            </div>
          </div>
          <div
            v-if="!isChapterCollapsed(row.chapter)"
            class="zw-board-band-grid"
            role="list"
          >
            <StoryboardCard
              v-for="scene in row.chapter.scenes"
              :key="scene.file"
              :reorder-enabled="!filtering"
              kind="scene"
              :title="sceneCustomName(scene)"
              :synopsis="scene.synopsis"
              :opening="
                sceneOpenings.openingFor(
                  scenePath(row.chapter.folder, scene.file),
                )
              "
              :synopsis-id="`${row.chapter.folder}/${scene.file}`"
              :entity-chips="
                entityChipsFor(scenePath(row.chapter.folder, scene.file))
              "
              :drag-source="
                sceneDragSource(row.chapter, scene.file, row.actFolder)
              "
              :is-drag-over="
                isDragOverKey(
                  sceneDragSource(row.chapter, scene.file, row.actFolder).key,
                )
              "
              :is-drag-source="
                isDragFromKey(
                  sceneDragSource(row.chapter, scene.file, row.actFolder).key,
                )
              "
              :on-chip-click="onChipClick"
              :on-arc-chip-pointer-down="onArcChipPointerDown"
              :on-commit-synopsis="
                (_id: string, value: string) =>
                  workspace.onUpdateSceneSynopsis(
                    row.chapter.folder,
                    scene.file,
                    value,
                  )
              "
              :on-rename="
                (to: string) =>
                  workspace.onRenameProjectScene(
                    row.chapter.folder,
                    scene.file,
                    to,
                  )
              "
              :on-card-pointer-down="onCardPointerDown"
              :on-keyboard-move="
                (direction: Direction) =>
                  onSceneKeyboardMove(row.chapter, scene.file, direction)
              "
              :on-park="
                () => workspace.onParkScene(row.chapter.folder, scene.file)
              "
              :on-delete="
                () =>
                  workspace.onDeleteProjectScene(row.chapter.folder, scene.file)
              "
              :on-add-scene="nothing"
              @open="openScene(scenePath(row.chapter.folder, scene.file))"
              @autosize="scrollSelectionIntoBoard"
            />
          </div>
          <div
            v-if="!filtering && !isChapterCollapsed(row.chapter)"
            class="zw-board-tail-row"
          >
            <div
              class="zw-board-tail-drop"
              :class="{
                'zw-board-tail-drag-over': isDragOverKey(
                  tailKeyFor(row.chapter),
                ),
              }"
              :data-board-key="tailKeyFor(row.chapter)"
              data-board-kind="chapter-tail"
              :data-board-folder="row.chapter.folder"
              :data-board-act="toDomAttr(row.actFolder)"
              :data-board-band="row.chapter.folder"
              aria-hidden="true"
            />
            <button
              type="button"
              class="zw-board-tail"
              :data-board-folder="row.chapter.folder"
              aria-label="Add scene"
              title="Add scene"
              @click="addSceneToChapter(row.chapter.folder)"
            >
              + Scene
            </button>
          </div>
        </section>
      </template>

      <!-- An act at the end of the board owns every card below its header, so
         without this there'd be no way to drop a chapter (or act) after it
         as ungrouped. Same affordance as the outline's end-of-list zone.
         Sits above the + New buttons so it isn't cramped against the scroll
         edge, and stays tall enough that the nearest-cell gutter can find it. -->
      <div
        v-if="
          isDraggingKind('act') ||
          showChapterBoundary(END_OF_MANUSCRIPT_DROP_TARGET)
        "
        class="zw-storyboard-end-drop"
        :class="{
          'zw-storyboard-end-drop-over': isDragOverKey(
            END_OF_MANUSCRIPT_DROP_TARGET,
          ),
        }"
        :data-board-key="END_OF_MANUSCRIPT_DROP_TARGET"
        data-board-kind="end-of-manuscript"
        :data-board-folder="END_OF_MANUSCRIPT_DROP_TARGET"
      >
        <span class="zw-sr-only">Move to the end, outside any act</span>
      </div>

      <div v-if="!filtering" class="zw-storyboard-new-items">
        <StoryboardCreate
          v-if="showUngroupedChapterCreate"
          kind="Chapter"
          @create="workspace.onNewStoryboardChapter($event, nothing)"
        />
        <StoryboardCreate
          kind="Act"
          @create="workspace.onNewStoryboardAct($event)"
        />
      </div>

      <div class="zw-sr-only" role="status" aria-live="polite">
        {{ model.announcement }}
      </div>
    </div>
  </div>
</template>

<style scoped>
.zw-storyboard-filtered :deep(.zw-board-card-chapter .zw-board-card-grip),
.zw-storyboard-filtered .zw-board-act-grip,
.zw-storyboard-filtered .zw-board-act-collapse,
.zw-storyboard-filtered .zw-board-band-collapse {
  visibility: hidden;
}
.zw-storyboard-filtered .zw-storyboard-end-drop {
  display: none;
}

.zw-storyboard-board {
  --timeline-axis: 33%;
  height: 100%;
  overflow: auto;
  padding: 24px 32px 64px;
  background: var(--bg);
  container-type: inline-size;
}
.zw-storyboard-loading {
  position: relative;
}
.zw-timeline-loading {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--space-sm);
  padding: var(--space-lg);
  color: var(--text-muted);
  opacity: 0.5;
  font-family: var(--font-heading);
  font-size: 0.875rem;
  text-align: center;
  pointer-events: none;
}
.zw-timeline-loading :deep(svg) {
  width: 1.25rem;
  height: 1.25rem;
}
.zw-storyboard-heading {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-sm) var(--space-lg);
  align-items: baseline;
  padding-bottom: 24px;
}
.zw-storyboard-heading h1 {
  font-family: var(--font-heading);
  font-size: 1.5rem;
  margin: 0;
}
.zw-storyboard-counts {
  margin-left: auto;
  color: var(--text-muted);
  font-size: 0.8125rem;
}
.zw-storyboard-timeline {
  position: relative;
  padding-top: 24px;
}
.zw-storyboard-timeline::before {
  content: "";
  position: absolute;
  top: 60px;
  bottom: 24px;
  left: var(--timeline-axis);
  width: 1px;
  background: var(--line);
}
.zw-storyboard-board-dragging,
.zw-storyboard-board-dragging :deep(*) {
  cursor: grabbing !important;
  user-select: none !important;
}
.zw-board-act-header,
.zw-board-band-header-wrap {
  position: relative;
  display: flex;
  align-items: center;
  min-height: 72px;
}
.zw-board-act-header::before,
.zw-board-band-header-wrap::before {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  height: 1px;
  background: var(--line);
}
.zw-board-act-title {
  position: relative;
  margin-left: var(--timeline-axis);
  transform: translateX(-50%);
  padding: 0 12px;
  background: var(--bg);
  font-family: var(--font-heading);
  font-size: 1.3rem;
  min-width: 0;
}
.zw-board-act-collapse {
  position: absolute;
  left: var(--timeline-axis);
  transform: translateX(calc(-100% - 12px));
  z-index: 2;
}
.zw-board-act-title {
  display: flex;
  align-items: center;
}
.zw-board-act-collapse,
.zw-board-band-collapse,
.zw-board-act-grip,
.zw-board-act-delete {
  border: 0;
  background: var(--bg);
  color: var(--text-muted);
  padding: 4px;
  cursor: pointer;
}
.zw-board-act-grip {
  cursor: grab;
}
.zw-board-act-delete {
  position: relative;
}
.twist-open {
  transform: rotate(90deg);
}
.zw-board-act-header-drag {
  opacity: 0.5;
}
.zw-board-act-header-over::before {
  background: var(--color-zw-amber);
}
.zw-board-band {
  position: relative;
}
.zw-board-band-header {
  position: relative;
  margin-left: var(--timeline-axis);
  transform: translateX(-50%);
  width: max-content;
}
.zw-board-band-collapse {
  position: absolute;
  left: var(--timeline-axis);
  top: 50%;
  transform: translate(-100%, -50%);
  z-index: 2;
}
.zw-board-act-count,
.zw-board-band-collapse-count {
  position: absolute;
  top: 30px;
  left: 0;
  white-space: nowrap;
  font-size: 0.75rem;
}
.zw-board-band-grid {
  display: flex;
  flex-direction: column;
}
.zw-board-tail-row {
  position: relative;
  padding: 16px 0 24px calc(var(--timeline-axis) + 24px);
}
.zw-board-tail {
  border: 0;
  background: transparent;
  color: var(--text-muted);
  font-size: 0.8125rem;
  padding: 6px 0;
  cursor: pointer;
}
.zw-board-tail:hover {
  color: var(--text);
}

.zw-board-tail-drop {
  position: absolute;
  top: 0;
  left: var(--timeline-axis);
  right: 0;
  height: 12px;
}
.zw-board-tail-drag-over::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: var(--color-zw-amber);
}
.zw-storyboard-end-drop {
  position: relative;
  height: 28px;
  margin: 8px 0;
}
.zw-storyboard-end-drop::before {
  content: "";
  position: absolute;
  top: 14px;
  left: 0;
  right: 0;
  height: 1px;
  background: var(--line);
}
.zw-storyboard-end-drop-over::before {
  height: 2px;
  background: var(--color-zw-amber);
}
.zw-storyboard-end-drop-idle::before {
  visibility: hidden;
}
.zw-board-act-end {
  position: relative;
}
.zw-board-act-end > .zw-storyboard-end-drop {
  position: absolute;
  top: -22px;
  left: 0;
  right: 0;
  margin: 0;
}
.zw-board-act-end-label {
  position: relative;
  display: flex;
  align-items: center;
  min-height: 36px;
  margin-bottom: 20px;
  color: var(--text-muted);
  font-size: 0.75rem;
}
.zw-board-act-end-label span {
  position: relative;
  margin-left: var(--timeline-axis);
  transform: translateX(-50%);
  padding: 4px 12px;
  background: var(--bg);
  white-space: nowrap;
}
.zw-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}
@container (max-width:560px) {
  .zw-storyboard-timeline {
    --timeline-axis: 12%;
  }
  .zw-board-act-title,
  .zw-board-band-header {
    transform: none;
  }
  .zw-board-act-title {
    padding-left: 24px;
  }
}
.zw-board-act-header,
.zw-board-band-header-wrap {
  display: grid;
  grid-template-columns: 66% 1fr;
  padding-block: var(--space-sm);
}
.zw-timeline-heading-group {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 0;
  transform: none;
  justify-self: safe center;
  width: max-content;
  max-width: 75cqw;
  min-width: 0;
  padding: 0 12px;
  background: var(--bg);
}
.zw-timeline-heading-group .zw-board-act-title,
.zw-timeline-heading-group .zw-board-band-header {
  margin-left: 0;
  transform: none;
  padding: 0;
}
.zw-timeline-heading-group .zw-board-act-grip,
.zw-timeline-heading-group .zw-board-act-delete {
  flex-shrink: 0;
}
.zw-timeline-heading-group .zw-board-act-collapse,
.zw-timeline-heading-group .zw-board-band-collapse {
  position: static;
  transform: none;
  flex-shrink: 0;
}
.zw-board-band-collapse {
  order: -1;
}
@container (max-width:560px) {
  .zw-board-act-header,
  .zw-board-band-header-wrap {
    grid-template-columns: 100%;
  }
  .zw-timeline-heading-group {
    max-width: 88cqw;
    justify-self: start;
    margin-left: var(--timeline-axis);
  }
}
.zw-board-act-grip {
  width: 24px;
  height: 24px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.zw-board-act-delete {
  margin-left: 16px;
  padding: 0;
  font: inherit;
  font-size: 0.75rem;
}
.zw-board-act-grip,
.zw-board-act-delete {
  opacity: 0;
  pointer-events: none;
}
.zw-timeline-heading-group:hover .zw-board-act-grip,
.zw-timeline-heading-group:hover .zw-board-act-delete,
.zw-board-act-header:focus-within .zw-board-act-grip,
.zw-board-act-header:focus-within .zw-board-act-delete {
  opacity: 1;
  pointer-events: auto;
}
@media (hover: none) {
  .zw-board-act-grip,
  .zw-board-act-delete {
    opacity: 1;
    pointer-events: auto;
  }
}
</style>
